import { Button, ButtonProps } from '@/components/ui/Button';
import { ConditionalWrapper } from '@/components/ui/ConditionalWrapper';
import { cn } from '@/lib/helpers/utils';
import { navigate } from 'gatsby';
import isArray from 'lodash/isArray';
import { ArchiveX, CalendarDays } from 'lucide-react';
import React from 'react';
import { daysBetween } from '../../../app/utils/date';
import { ContentType, Opportunity } from '../../../types/content';
import { OpportunityCurrency, OpportunityDetails, OpportunitySalary } from '../../../types/opportunity';
import Save from '../../bookmarks/Save';
import { DaysLeftToApplyPill, DaysLeftToApplyPillProps } from '../utils/DaysLeftToApplyPill';
import { ImageTypeToImage } from '../utils/Image';
import { OrganisationPlaceHolderLogo } from '../utils/Logo';
import ApplyToOpportunity from './actions/ApplyToOpportunity';

const formatSalaryWithCurency = (currency: string, salary: number) => {
  const formatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  });
  return formatter.format(salary);
};

export const formatOpportunitySalary = (opportunity: any): OpportunitySalary => {
  const salaryParts: OpportunitySalary = {
    upper: null,
    lower: null,
    currency: OpportunityCurrency.GBP,
    display: null
  };
  const salaryRange: any =
    opportunity.salary?.ranges ??
    (opportunity.relationships?.field_salary_range
      ? opportunity.relationships?.field_salary_range.map((salary: any) => salary.label)
      : null);
  const salaryUpper: number = opportunity.salary?.upper ?? opportunity.field_salary_upper;
  const salaryLower: number = opportunity.salary?.lower ?? opportunity.field_salary_lower;
  const salaryCurrency = opportunity.salary?.currency ?? opportunity.relationships?.field_currency?.label;
  if (salaryUpper || salaryLower) {
    salaryParts.upper = salaryUpper;
    salaryParts.lower = salaryLower;
    salaryParts.currency = salaryCurrency
      ? (OpportunityCurrency as any)[salaryCurrency.toUpperCase()]
      : salaryParts.currency;
    if (salaryUpper === salaryLower) {
      salaryParts.display = `${
        salaryParts.currency ? formatSalaryWithCurency(salaryParts.currency, salaryUpper) : `${salaryUpper}`
      }`;
    } else if (salaryUpper && salaryLower) {
      salaryParts.display = `${
        salaryParts.currency ? formatSalaryWithCurency(salaryParts.currency, salaryLower) : `${salaryLower}`
      } - ${salaryParts.currency ? formatSalaryWithCurency(salaryParts.currency, salaryUpper) : `${salaryUpper}`}`;
    } else if (!salaryUpper) {
      salaryParts.display = `${
        salaryParts.currency ? formatSalaryWithCurency(salaryParts.currency, salaryLower) : `${salaryLower}`
      }`;
    } else if (!salaryLower) {
      salaryParts.display = `${
        salaryParts.currency ? formatSalaryWithCurency(salaryParts.currency, salaryUpper) : `${salaryUpper}`
      }`;
    }
  } else {
    if (salaryRange) {
      const rawRanges = isArray(salaryRange) ? salaryRange : [salaryRange];
      const rangesArray: Array<number> = [];
      let rangePlus: Boolean = false;
      rawRanges.map((ranges: any) => {
        ranges.split(' ').map((range: string) => {
          range.indexOf('+') > -1 && (rangePlus = true);
          const rangeValue = range.replace(/\D/g, '');
          rangeValue.length > 0 && rangesArray.push(parseInt(rangeValue));
        });
      });
      const sortedRangesArray: Array<number> = rangesArray.sort(function (a, b) {
        return a - b;
      });
      if (sortedRangesArray.length > 0) {
        sortedRangesArray.length === 1 && (salaryParts.upper = sortedRangesArray[0]);
        if (sortedRangesArray.length > 1) {
          salaryParts.lower = sortedRangesArray[0];
          salaryParts.upper = sortedRangesArray[sortedRangesArray.length - 1];
          salaryParts.display = `${formatSalaryWithCurency(
            salaryParts.currency,
            salaryParts.lower as number
          )} - ${formatSalaryWithCurency(salaryParts.currency, salaryParts.upper as number)}`;
        } else {
          salaryParts.display = `${formatSalaryWithCurency(salaryParts.currency, salaryParts.upper as number)}`;
        }
        // H@ck for £40,000 ref data item, this is until we have proper salary data.
        rangePlus && (salaryParts.display = salaryParts.display.replace('40,000', '40,000+'));
      }
    }
  }

  return salaryParts;
};

export const formatOpportunityDaysLeftToApply = (
  opportunity: any,
  asString: boolean = true,
  bookmarkInfo?: any
): string | JSX.Element | null => {
  // Pre-register flag is set, take priority
  if (opportunity.preregister) {
    const isPreRegistered: any = bookmarkInfo?.bookmarks[opportunity.uuid]
      ? Object.keys(bookmarkInfo?.bookmarks[opportunity.uuid].state).length > 0
      : null;
    const buttonText: string = isPreRegistered ? 'Interest registered' : 'Register your interest';
    return asString ? (
      `<div class="font-semibold text-gray text-xs line-clamp-1 !inline-flex px-3 py-1 rounded-full">${buttonText}</div>`
    ) : (
      <span className="font-semibold text-gray text-xs line-clamp-1 !inline-flex px-3 py-1 rounded-full">
        {buttonText}
      </span>
    );
  }
  const applicationDeadlineDate = opportunity.applicationDeadline
    ? new Date(opportunity.applicationDeadline * 1000)
    : opportunity.field_application_deadline
    ? new Date(opportunity.field_application_deadline)
    : null;
  if (applicationDeadlineDate || (opportunity.applicationOpen != undefined && !opportunity.applicationOpen)) {
    const daysLeft: number = daysBetween(new Date(), applicationDeadlineDate as Date);
    return displayDaysLeftToApply(daysLeft, opportunity.applicationOpen, 'apply', asString, ContentType.Opportunity);
  }
  return null;
};

export const displayDaysLeftToApply = (
  daysLeft: number,
  applicationOpen: boolean | null,
  action: string,
  asString: boolean = true,
  contentType: ContentType | null = null
) => {
  if (daysLeft > 0 && daysLeft < 30 && (applicationOpen == true || applicationOpen == undefined)) {
    const classes = 'font-semibold text-gray text-xs line-clamp-1 !inline-flex px-3 py-1 rounded-full';

    if (daysLeft === 1) {
      return asString ? (
        `<div class="${classes} bg-warning">Last day to ${action}</div>`
      ) : (
        <span className={`${classes} bg-warning`}>Last day to {action}</span>
      );
    } else if (daysLeft > 1) {
      return asString ? (
        `<div class="${classes} bg-warning-200">${daysLeft} days to ${action}</div>`
      ) : (
        <span className={`${classes} bg-warning-200`}>
          {daysLeft} days to {action}
        </span>
      );
    }
  }

  if (contentType === 'opportunity' && (daysLeft < 0 || (applicationOpen != undefined && !applicationOpen))) {
    return asString ? (
      `<div class="font-semibold text-white text-xs line-clamp-1 !inline-flex px-3 py-1 rounded-full bg-danger">Expired</div>`
    ) : (
      <span className="font-semibold text-white text-xs line-clamp-1 !inline-flex px-3 py-1 rounded-full bg-danger">
        <CalendarDays className="w-3 h-3 relative -top-[0.1rem]" />
        Expired
      </span>
    );
  }
  return null;
};

/**
 * *********************************
 * NEW BELOW, ABOVE REMOVE AS REFACTOR
 * *********************************
 */

interface OpportunityOrganisationLogoProps extends React.HTMLAttributes<HTMLDivElement> {
  opportunity: Opportunity | Event;
  imageClassName?: string;
}

export const OpportunityOrganisationLogo = ({
  opportunity,
  imageClassName = '',
  className
}: OpportunityOrganisationLogoProps): React.ReactNode => {
  return (
    <ConditionalWrapper
      condition={opportunity.organisation?.status && opportunity.organisation?.path ? true : false}
      wrapper={(children: React.ReactNode) => (
        <Button
          variant="bare"
          data-cy={`link:view-organisation`}
          onClick={() => navigate(opportunity.organisation?.path)}
          aria-label={`Image link to view ${opportunity.organisation?.title}`}
        >
          {children}
        </Button>
      )}
    >
      <div className={cn('border border-gray-200 rounded bg-background flex-shrink-0 flex items-center', className)}>
        {opportunity.organisation?.logo ? (
          <ImageTypeToImage image={opportunity.organisation?.logo} className={imageClassName} />
        ) : (
          <OrganisationPlaceHolderLogo
            organisationName={opportunity.organisation?.title ?? opportunity.sourceOrganisationName ?? '?'}
            className={cn('h-full', imageClassName)}
          />
        )}
      </div>
    </ConditionalWrapper>
  );
};

interface OpportunityDaysLeftToApplyPillProps extends DaysLeftToApplyPillProps {}

export const OpportunityDaysLeftToApplyPill = ({ daysLeft, ...props }: OpportunityDaysLeftToApplyPillProps) => {
  let daysLeftLabel: React.ReactNode = <span>{daysLeft} days to apply</span>;
  if (daysLeft === 1) {
    daysLeftLabel = <span>Last day to apply</span>;
  } else if (daysLeft <= 0) {
    daysLeftLabel = (
      <>
        <ArchiveX className="w-3.5 h-3.5" />
        <span>Expired</span>
      </>
    );
  }

  return (
    <DaysLeftToApplyPill daysLeft={daysLeft} {...props}>
      {daysLeftLabel}
    </DaysLeftToApplyPill>
  );
};

interface OpportunityActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  opportunity: Opportunity;
  opportunityDetails: OpportunityDetails;
  location: Location;
  buttonProps?: Omit<ButtonProps, 'children'>;
  applicationAdviceModalTrackingLabel?: string; // Has user come from the Application Advice modal if so pass to tracking
}

export const OpportunityActions = ({
  opportunity,
  opportunityDetails,
  location,
  buttonProps = { size: 'sm' },
  applicationAdviceModalTrackingLabel,
  ...props
}: OpportunityActionsProps) => {
  return (
    <div {...props}>
      {(opportunity.applicationOpen || opportunity.preregister) && (
        <ApplyToOpportunity
          opportunity={opportunity}
          location={location}
          {...buttonProps}
          duplicateTrackEventName={
            applicationAdviceModalTrackingLabel ? 'application_modal_' + applicationAdviceModalTrackingLabel : undefined
          }
        />
      )}
      {!applicationAdviceModalTrackingLabel && (
        <Save
          id={opportunity.uuid}
          bookmarkBtnId={`oppSave_${opportunity.uuid}`}
          contentType={ContentType.Opportunity}
          {...buttonProps}
        />
      )}
    </div>
  );
};
